import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import {
  InterventionDataPromiseLookupService,
  BfxBlotterAction,
  ContextMenuItemState,
  SystemConfigPromiseService,
  TraderGroupPromiseLookupService,
  ActiveUserProfilePromiseService,
  ActiveUser,
  Roles,
  BaseLookupService,
} from "shared/cubiq-services";

import { BfxBaseBlotter } from "../../bfx-base-blotter/bfx-base-blotter";
import {
  BfxBaseBlotterContextMenuService,
  BfxLadderBlotterContextMenuEventType,
  BfxLadderBlotterContextMenuEvent,
  BfxBlotterActionEvent,
  BfxBlotterActionSubject,
} from "../../bfx-base-blotter/bfx-base-blotter-contextmenu.service";
import {
  BfxContextMenuItemMode,
  //
  SALES_APPROVAL_REASONS,
  SALES_REJECT_REASONS,
  TRADER_ACCEPT_REJECT_REASONS,
  CANCEL_REASONS,
  AMEND_MARKUP_REASONS,
  //
  ITEM_STATE_AVAILABLE_MODE_MAPPING,
  ITEM_STATE_ENABLED_MODE_MAPPING,
  ITEM_STATE_ENABLED_MUST_BE_PESSIMISTIC,
  FILL_APPROVE_REASONS,
  FILL_CANCEL_REASONS,
  FILL_CLEAR_REASONS,
  FWDROLL_RETRY_REASONS,
} from "../models/bfx-intervention-contextmenu.const";
import {
  BfxInterventionContextMenuItem,
  BfxLadderBlotterInterventionContextMenuEvent,
  BfxBlotterInterventionContextMenuState,
} from "../models/bfx-intervention-contextmenu.models";

export const MODIFY_LOCK_IGNORE_STATES: Array<CubiqTS.OrderState> = [
  CubiqTS.OrderState.ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED,
];

@Injectable()
export class BfxInterventionContextMenuService extends BfxBaseBlotterContextMenuService<any> {
  /**********************************************************************/
  /* DATA */
  public menuItems: Array<BfxInterventionContextMenuItem> = [];
  protected menuState = new BfxBlotterInterventionContextMenuState();
  private activeUser: ActiveUser;

  /*     protected permissions: {
		canEnterMarkupBmark: boolean,
		canEnterMarkupAlgo: boolean,
		canEnterMarkupLimits: boolean,
		canAcceptAsTrader: boolean,
		canRejectAsTrader: boolean,
		canAcceptUnrestrictedAsTrader: boolean,
		canApproveAsSales: boolean,
		canCancelOrder: boolean,
		canRejectAsSales: boolean,
		canApproveUnrestrictedAsSales: boolean,
		canCancelAndModifyOrderUnrestricted: boolean,
		canUnlockOrders: boolean,
		canAmendMarkup: boolean,
		canPriceFixRate: boolean,
    } = {
    
    } */

  /**********************************************************************/
  /* CONSTRUCTOR */
  constructor(
    protected interventionDataService: InterventionDataPromiseLookupService,
    protected systemConfigService: SystemConfigPromiseService,
    protected traderGroupService: TraderGroupPromiseLookupService,
    protected activeUserService: ActiveUserProfilePromiseService,
  ) {
    super(new BfxBlotterActionSubject());

    let contextmenuPromises: Array<PromiseLike<void>> = [
      this.systemConfigService.hasRole(Roles.DI_TRADER_ACCEPT).then((value) => {
        logger.out.info(`BfxBlotterInterventionContextMenuService: DI_TRADER_ACCEPT ${value}`);
        this.menuState.canAcceptAsTrader = value;
      }),

      this.systemConfigService.hasRole(Roles.DI_TRADER_REJECT).then((value) => {
        logger.out.info(`BfxBlotterInterventionContextMenuService: DI_TRADER_REJECT ${value}`);
        this.menuState.canRejectAsTrader = value;
      }),

      this.systemConfigService.hasRole(Roles.DI_TRADER_UNRESTRICTED).then((value) => {
        logger.out.info(
          `BfxBlotterInterventionContextMenuService: DI_TRADER_UNRESTRICTED ${value}`,
        );
        this.menuState.hasDITraderUnrestricted = value;
      }),

      this.systemConfigService.hasRole(Roles.DI_SALES_APPROVE).then((value) => {
        logger.out.info(`BfxBlotterInterventionContextMenuService: DI_SALES_APPROVE ${value}`);
        this.menuState.canApproveAsSales = value;
      }),

      this.systemConfigService.hasRole(Roles.ORDER_CANCEL).then((value) => {
        logger.out.info(`BfxBlotterInterventionContextMenuService: ORDER_CANCEL ${value}`);
        this.menuState.canCancelOrder = value;
      }),

      this.systemConfigService.hasRole(Roles.DI_SALES_REJECT).then((value) => {
        logger.out.info(`BfxBlotterInterventionContextMenuService: DI_SALES_REJECT ${value}`);
        this.menuState.canRejectAsSales = value;
      }),

      this.systemConfigService.hasRole(Roles.DI_SALES_UNRESTRICTED).then((value) => {
        logger.out.info(`BfxBlotterInterventionContextMenuService: DI_SALES_UNRESTRICTED ${value}`);
        this.menuState.canApproveUnrestrictedAsSales = value;
      }),

      this.systemConfigService.hasRole(Roles.ORDER_UNRESTRICTED_CANCEL_AND_MODIFY).then((value) => {
        logger.out.info(
          `BfxBlotterInterventionContextMenuService: ORDER_UNRESTRICTED_CANCEL_AND_MODIFY ${value}`,
        );
        this.menuState.canCancelAndModifyOrderUnrestricted = value;
      }),

      this.systemConfigService.hasRole(Roles.ORDER_UNLOCK).then((value) => {
        logger.out.info(`BfxBlotterInterventionContextMenuService: ORDER_UNLOCK ${value}`);
        this.menuState.canUnlockOrders = value;
      }),

      this.systemConfigService.hasRole(Roles.DI_SALES_AMEND_MARKUP).then((value) => {
        logger.out.info(`BfxBlotterInterventionContextMenuService: DI_SALES_AMEND_MARKUP ${value}`);
        this.menuState.canAmendMarkup = value;
      }),

      this.systemConfigService.hasRole(Roles.ORDER_ENTRY_MARKUP_BENCHMARK).then((value) => {
        logger.out.info(
          `BfxBlotterInterventionContextMenuService: ROLE_ORDER_ENTRY_MARKUP_BENCHMARK ${value}`,
        );
        this.menuState.canEnterMarkupBmark = value;
      }),
      this.systemConfigService.hasRole(Roles.ORDER_ENTRY_MARKUP_LIMIT).then((value) => {
        logger.out.info(
          `BfxBlotterInterventionContextMenuService: ROLE_ORDER_ENTRY_MARKUP_LIMIT ${value}`,
        );
        this.menuState.canEnterMarkupLimits = value;
      }),

      this.systemConfigService.hasRole(Roles.ORDER_ENTRY_MARKUP_ALGO).then((value) => {
        logger.out.info(
          `BfxBlotterInterventionContextMenuService: ROLE_ENTRY_MARKUP_ALGO ${value}`,
        );
        this.menuState.canEnterMarkupAlgo = value;
      }),

      this.systemConfigService.hasRole(Roles.ORDER_FIX_RATE).then((value) => {
        logger.out.info(`BfxBlotterInterventionContextMenuService: ORDER_FIX_RATE ${value}`);
        this.menuState.canPriceFixRate = value;
      }),

      this.systemConfigService.hasRole(Roles.DI_APPROVE_FILL).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: DI_APPROVE_FILL ${value}`);
        this.menuState.canDIApproveFill = value;
      }),

      this.systemConfigService.hasRole(Roles.DI_CANCEL_FILL).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: DI_CANCEL_FILL ${value}`);
        this.menuState.canDICancelFill = value;
      }),

      this.systemConfigService.hasRole(Roles.DI_CLEAR_FILL).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: DI_CANCEL_FILL ${value}`);
        this.menuState.canDIClearFill = value;
      }),

      this.systemConfigService.hasRole(Roles.DI_SALES_CLEAR).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: DI_SALES_CLEAR ${value}`);
        this.menuState.canClearAsSales = value;
        this.menuState.canClearFwdRoll = value;
      }),

      this.systemConfigService.hasRole(Roles.DI_SALES_PRICE).then((value) => {
        logger.out.info(`BfxBlotterOrdersContextMenuService: DI_SALES_PRICE ${value}`);
        this.menuState.canPriceFwdRoll = value;
        this.menuState.canRetryFwdRoll = value;
      }),

      this.activeUserService.getActiveUserData().then(
        (activeUser: ActiveUser) => {
          this.activeUser = activeUser;
          this.menuState.isTrader = CubiqTS.UserType.TRADER === activeUser.userTypeId;
          this.menuState.isSales = CubiqTS.UserType.SALES === activeUser.userTypeId;
          this.menuState.isClient = CubiqTS.UserType.CLIENT === activeUser.userTypeId;
        },
        (reason) => {
          logger.out.error(`BfxBlotterInterventionContextMenuService: ActiveUser ${reason}`);
        },
      ),
    ];
  }

  /**********************************************************************/
  /* METHODS */

  public getMenuItems(): Array<BfxInterventionContextMenuItem> {
    return this.menuItems;
  }

  protected prepareActions(ladderBlotter: BfxBaseBlotter): void {
    // nothing to add
  }

  public buildMenu(
    blotter: any,
    pageX: number,
    pageY: number,
    selectedIds: Array<string>,
    selectedItems?: Array<CubiqTS.InterventionEventImpl> | null,
  ): void {
    let base = this.interventionDataService.getBase() as BaseLookupService<
      string,
      CubiqTS.InterventionEventImpl
    >;
    if (selectedItems == null) {
      selectedItems = []; // : Array<CubiqTS.InterventionEventImpl>
      for (let i = 0; i < selectedIds.length; i++) {
        let item = base.getItem(selectedIds[i]);

        if (item instanceof CubiqTS.InterventionEventImpl) {
          selectedItems.push(item);
        } else {
          selectedItems.push(null);
        }
      }

      this.setSelectedItems(selectedItems, blotter);
    }

    let menuItems = this.createMenuItems();
    if (Array.isArray(selectedItems) && selectedItems.length > 0) {
      this.menuState.count = selectedItems.length;
      for (let selectedItem of selectedItems) {
        for (let [actionId, menuItem] of menuItems) {
          switch (actionId) {
            case BfxBlotterAction.ACTION_DI_ACCEPT:
              let acceptAvailableEnabled = this.checkTraderAcceptAvailableEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, acceptAvailableEnabled);
              this.applySelectedItemToMenuItem(
                menuItem,
                acceptAvailableEnabled,
                acceptAvailableEnabled,
              );
              break;
            case BfxBlotterAction.ACTION_DI_TRADER_REJECT:
              let traderRejectAvailableEnabled =
                this.checkTraderRejectAvailableEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, traderRejectAvailableEnabled);
              this.applySelectedItemToMenuItem(
                menuItem,
                traderRejectAvailableEnabled,
                traderRejectAvailableEnabled,
              );
              break;
            case BfxBlotterAction.ACTION_DI_APPROVE:
              let approveAvailableEnabled = this.checkSalesApproveAvailableEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, approveAvailableEnabled);
              this.applySelectedItemToMenuItem(
                menuItem,
                approveAvailableEnabled,
                approveAvailableEnabled,
              );
              break;
            case BfxBlotterAction.ACTION_DI_REJECT:
              let salesRejectAvailableEnabled = this.checkSalesRejectAvailableEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, salesRejectAvailableEnabled);
              this.applySelectedItemToMenuItem(
                menuItem,
                salesRejectAvailableEnabled,
                salesRejectAvailableEnabled,
              );
              break;
            case BfxBlotterAction.ACTION_DI_CANCEL:
              let cancelAvailable = this.checkCancelAvailable(selectedItem);
              let cancelEnabled = this.checkCancelEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, cancelEnabled);
              this.applySelectedItemToMenuItem(menuItem, cancelAvailable, cancelEnabled);
              break;
            case BfxBlotterAction.ACTION_DI_AMEND_MARKUP:
              let amendMarkupAvailable = this.menuState.canAmendMarkup;
              let amendMarkupEnabled = this.checkAmendMarkupEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, amendMarkupEnabled);
              this.applySelectedItemToMenuItem(menuItem, amendMarkupAvailable, amendMarkupEnabled);
              break;
            case BfxBlotterAction.ACTION_DI_MANUAL_PRICE:
              let manualPriceAvailable = this.menuState.canPriceFixRate;
              let manualPriceEnabled = this.checkManualPriceEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, manualPriceEnabled);
              this.applySelectedItemToMenuItem(menuItem, manualPriceAvailable, manualPriceEnabled);
              break;
            case BfxBlotterAction.ACTION_DI_FWD_ROLL_PRICE:
              let fwdRollPriceAvailable = this.menuState.canPriceFwdRoll;
              let fwdRollPriceEnabled = this.checkFwdRollPriceEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, fwdRollPriceAvailable);
              this.applySelectedItemToMenuItem(
                menuItem,
                fwdRollPriceAvailable,
                fwdRollPriceEnabled,
              );
              break;
            case BfxBlotterAction.ACTION_DI_FWD_ROLL_CLEAR:
              let fwdRollClearAvailable = this.menuState.canClearFwdRoll;
              let fwdRollClearEnabled = this.checkFwdRollClearEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, fwdRollClearAvailable);
              this.applySelectedItemToMenuItem(
                menuItem,
                fwdRollClearAvailable,
                fwdRollClearEnabled,
              );
              break;
            case BfxBlotterAction.ACTION_DI_FWD_ROLL_RETRY:
              let fwdRollRetryAvailable = this.menuState.canRetryFwdRoll;
              let fwdRollRetryEnabled = this.checkFwdRollRetryEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, fwdRollRetryAvailable);
              this.applySelectedItemToMenuItem(
                menuItem,
                fwdRollRetryAvailable,
                fwdRollRetryEnabled,
              );
              break;
            case BfxBlotterAction.ACTION_DI_UNLOCK: // modify for unlocking fixing rate events
              let unlockAvailable = this.menuState.canUnlockOrders;
              let unlockEnabled = this.checkUnlockEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, unlockEnabled);
              this.applySelectedItemToMenuItem(menuItem, unlockAvailable, unlockEnabled);
              break;
            case BfxBlotterAction.ACTION_DI_VIEW_DETAILS:
              let viewDetailsAvailable = true;
              let viewDetailsEnabled = this.checkViewDetailsEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, viewDetailsEnabled);
              this.applySelectedItemToMenuItem(menuItem, viewDetailsAvailable, viewDetailsEnabled);
              break;
            case BfxBlotterAction.ACTION_DI_FILL_APPROVE:
              let diFillApproveAvailable = this.checkDIFillApproveAvailable(selectedItem);
              let diFillApproveEnabled = this.checkDIFillApproveEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, diFillApproveAvailable);
              this.applySelectedItemToMenuItem(
                menuItem,
                diFillApproveAvailable,
                diFillApproveEnabled,
              );
              break;
            case BfxBlotterAction.ACTION_DI_FILL_CANCEL:
              let diFillCancelAvailable = this.checkDIFillCancelAvailable(selectedItem);
              let diFillCancelEnabled = this.checkDIFillCancelEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, diFillCancelAvailable);
              this.applySelectedItemToMenuItem(
                menuItem,
                diFillCancelAvailable,
                diFillCancelEnabled,
              );
              break;
            case BfxBlotterAction.ACTION_DI_FILL_CLEAR:
              let diFillClearAvailable = this.checkDIFillClearAvailable(selectedItem);
              let diFillClearEnabled = this.checkDIFillClearEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, diFillClearAvailable);
              this.applySelectedItemToMenuItem(menuItem, diFillClearAvailable, diFillClearEnabled);
              break;
            default:
              logger.out.warn("Invalid action");
              break;
          }
        }
      }
    }
    this.prioritizeFwdRollItems(menuItems);
    this.menuItems = Array.from(menuItems.values());

    this.showMenuSubject.next(
      new BfxLadderBlotterInterventionContextMenuEvent(
        BfxLadderBlotterContextMenuEventType.MENU_SHOW,
        blotter,
        pageX,
        pageY,
        [],
        null,
        Array.from(menuItems.values()),
      ),
    );
  }

  /**
   * As the Fwd Roll and Manual Price items may have the same name weo don't want they both being available - Manual Price
   * will alwyas be shown unless FWD is explicitly available
   * @param menuItems
   */
  private prioritizeFwdRollItems(menuItems: Map<BfxBlotterAction, BfxInterventionContextMenuItem>) {
    const manualPrice = menuItems.get(BfxBlotterAction.ACTION_DI_MANUAL_PRICE);
    const fillClear = menuItems.get(BfxBlotterAction.ACTION_DI_FILL_CLEAR);
    const fwdRollPrice = menuItems.get(BfxBlotterAction.ACTION_DI_FWD_ROLL_PRICE);
    const fwdRollClear = menuItems.get(BfxBlotterAction.ACTION_DI_FWD_ROLL_CLEAR);

    if (fwdRollPrice.isAvailable && fwdRollPrice.isEnabled) {
      manualPrice.state.available = false;
      // Override enabled if more than one selected
      fwdRollPrice.state.enabled = this.menuState.count == 1;
    } else {
      fwdRollPrice.state.available = false;
    }
    if (fwdRollClear.isAvailable && fwdRollClear.isEnabled) {
      // Clear can work with more than one portfolio
      fillClear.state.available = false;
    } else {
      fwdRollClear.state.available = false;
    }
  }

  public fireActionEvent(
    actionId: BfxBlotterAction,
    ladderBlotter: any,
    selectedIds?: Array<string>,
    selectedItems?: Array<any>,
    itemState?: ContextMenuItemState,
    hide?: boolean,
    menuBoundingClientRect?: any,
    actionEvent?: any,
  ): void {
    let event: BfxBlotterActionEvent = new BfxBlotterActionEvent(
      actionId,
      ladderBlotter,
      this.getSelectedIds(selectedIds, ladderBlotter),
      this.getSelectedItems(selectedItems, ladderBlotter),
      itemState,
      hide,
      menuBoundingClientRect,
      actionEvent,
    );
    this.actionSubject.next(event);
  }

  protected applySelectedItemToMenuItem(
    menuItem: BfxInterventionContextMenuItem,
    itemAvailable: boolean,
    itemEnabled: boolean,
  ): void {
    let availableMode = ITEM_STATE_AVAILABLE_MODE_MAPPING.get(menuItem.actionId);
    let enabledMode = ITEM_STATE_ENABLED_MUST_BE_PESSIMISTIC.includes(menuItem.actionId)
      ? BfxContextMenuItemMode.PESSIMISTIC
      : ITEM_STATE_ENABLED_MODE_MAPPING.get(menuItem.actionId);
    menuItem.state.available =
      menuItem.state.available == null
        ? itemAvailable
        : availableMode === BfxContextMenuItemMode.PESSIMISTIC
        ? menuItem.state.available && itemAvailable
        : menuItem.state.available || itemAvailable;
    menuItem.state.enabled =
      menuItem.state.enabled == null
        ? itemEnabled
        : enabledMode === BfxContextMenuItemMode.PESSIMISTIC
        ? menuItem.state.enabled && itemEnabled
        : menuItem.state.enabled || itemEnabled;
  }

  protected addReferenceIdToMenuItem(
    menuItem: BfxInterventionContextMenuItem,
    item: CubiqTS.InterventionEventImpl,
    itemEnabled: boolean,
  ): void {
    if (
      menuItem instanceof BfxInterventionContextMenuItem &&
      item instanceof CubiqTS.InterventionEventImpl &&
      typeof itemEnabled === "boolean" &&
      itemEnabled
    ) {
      switch (menuItem.actionId) {
        case BfxBlotterAction.ACTION_DI_ACCEPT:
        case BfxBlotterAction.ACTION_DI_TRADER_REJECT:
          let acceptanceType = this.determineAcceptanceType(item.orderData);
          if (this.hasOrderId(item) && acceptanceType != null) {
            if (
              menuItem.state.traderReference.has(acceptanceType) &&
              Array.isArray(menuItem.state.traderReference.get(acceptanceType))
            ) {
              menuItem.state.traderReference.get(acceptanceType).push(this.getOrderId(item));
            } else {
              menuItem.state.traderReference.set(acceptanceType, [this.getOrderId(item)]);
            }
          }
          break;
        case BfxBlotterAction.ACTION_DI_MANUAL_PRICE:
          if (
            item.fixingRateRequirement instanceof CubiqTS.FixingRateRequirementDataImpl &&
            item.fixingRateRequirement.requirementId instanceof CubiqTS.Long
          ) {
            menuItem.state.referencedOrderIds.push(
              item.fixingRateRequirement.requirementId.toString(),
            );
          }
          break;
        case BfxBlotterAction.ACTION_DI_UNLOCK:
          if (
            item.fixingRateRequirement instanceof CubiqTS.FixingRateRequirementDataImpl &&
            item.fixingRateRequirement.requirementId instanceof CubiqTS.Long
          ) {
            menuItem.state.pricingReference.push(
              item.fixingRateRequirement.requirementId.toString(),
            );
          } else if (item?.fwdRollOrderData?.portfolioId) {
            menuItem.state.portfolioReferences.push({
              diEventId: item.eventId,
              portfolioId: item.fwdRollOrderData.portfolioId,
            });
          } else {
            menuItem.state.referencedOrderIds.push(this.getOrderId(item));
          }
          break;
        case BfxBlotterAction.ACTION_DI_CANCEL:
          if (this.hasOrderId(item)) {
            menuItem.state.referencedOrderIds.push(this.getOrderId(item));
            menuItem.state.referencedOrderData.push(item.orderData);
          }
          break;
        case BfxBlotterAction.ACTION_DI_FILL_APPROVE:
        case BfxBlotterAction.ACTION_DI_FILL_CANCEL:
        case BfxBlotterAction.ACTION_DI_FILL_CLEAR:
          if (this.hasOrderId(item)) {
            menuItem.state.referencedOrderIds.push(this.getOrderId(item));
            menuItem.state.referencedOrderData.push(item.orderData);
          }
          break;
        case BfxBlotterAction.ACTION_DI_FWD_ROLL_PRICE:
        case BfxBlotterAction.ACTION_DI_FWD_ROLL_CLEAR:
        case BfxBlotterAction.ACTION_DI_FWD_ROLL_RETRY:
          if (item?.fwdRollOrderData?.portfolioId) {
            menuItem.state.portfolioReferences.push({
              diEventId: item.eventId,
              portfolioId: item.fwdRollOrderData.portfolioId,
            });
          }
          break;
        default:
          if (this.hasOrderId(item)) {
            menuItem.state.referencedOrderIds.push(this.getOrderId(item));
          }
          break;
      }
    }
  }

  protected createMenuItems(): Map<BfxBlotterAction, BfxInterventionContextMenuItem> {
    let menuItems: Map<BfxBlotterAction, BfxInterventionContextMenuItem> = new Map();
    menuItems.set(
      BfxBlotterAction.ACTION_DI_ACCEPT,
      new BfxInterventionContextMenuItem(
        BfxBlotterAction.ACTION_DI_ACCEPT,
        "Accept",
        "fa icon-check order__menu__menuItem__icon__accept",
      ),
    );
    menuItems.set(
      BfxBlotterAction.ACTION_DI_APPROVE,
      new BfxInterventionContextMenuItem(
        BfxBlotterAction.ACTION_DI_APPROVE,
        "Approve",
        "fa icon-check order__menu__menuItem__icon__accept",
      ),
    );
    menuItems.set(
      BfxBlotterAction.ACTION_DI_FILL_APPROVE,
      new BfxInterventionContextMenuItem(
        BfxBlotterAction.ACTION_DI_FILL_APPROVE,
        "Approve Fill",
        "fa icon-check order__menu__menuItem__icon__accept",
      ),
    );
    menuItems.set(
      BfxBlotterAction.ACTION_DI_REJECT,
      new BfxInterventionContextMenuItem(
        BfxBlotterAction.ACTION_DI_REJECT,
        "Reject",
        "icon-close order__menu__menuItem__icon__reject",
      ),
    );
    menuItems.set(
      BfxBlotterAction.ACTION_DI_TRADER_REJECT,
      new BfxInterventionContextMenuItem(
        BfxBlotterAction.ACTION_DI_TRADER_REJECT,
        "Reject",
        "icon-close order__menu__menuItem__icon__reject",
      ),
    );
    menuItems.set(
      BfxBlotterAction.ACTION_DI_CANCEL,
      new BfxInterventionContextMenuItem(BfxBlotterAction.ACTION_DI_CANCEL, "Cancel"),
    );
    menuItems.set(
      BfxBlotterAction.ACTION_DI_FILL_CANCEL,
      new BfxInterventionContextMenuItem(BfxBlotterAction.ACTION_DI_FILL_CANCEL, "Cancel Fill"),
    );
    menuItems.set(
      BfxBlotterAction.ACTION_DI_AMEND_MARKUP,
      new BfxInterventionContextMenuItem(BfxBlotterAction.ACTION_DI_AMEND_MARKUP, "Amend Markup"),
    );
    menuItems.set(
      BfxBlotterAction.ACTION_DI_MANUAL_PRICE,
      new BfxInterventionContextMenuItem(BfxBlotterAction.ACTION_DI_MANUAL_PRICE, "Price"),
    );
    menuItems.set(
      BfxBlotterAction.ACTION_DI_FILL_CLEAR,
      new BfxInterventionContextMenuItem(BfxBlotterAction.ACTION_DI_FILL_CLEAR, "Clear"),
    );
    menuItems.set(
      BfxBlotterAction.ACTION_DI_FWD_ROLL_PRICE,
      new BfxInterventionContextMenuItem(
        BfxBlotterAction.ACTION_DI_FWD_ROLL_PRICE,
        "Price Fwd Roll",
      ),
    );
    menuItems.set(
      BfxBlotterAction.ACTION_DI_FWD_ROLL_CLEAR,
      new BfxInterventionContextMenuItem(
        BfxBlotterAction.ACTION_DI_FWD_ROLL_CLEAR,
        "Clear Fwd Roll",
      ),
    );
    menuItems.set(
      BfxBlotterAction.ACTION_DI_FWD_ROLL_RETRY,
      new BfxInterventionContextMenuItem(
        BfxBlotterAction.ACTION_DI_FWD_ROLL_RETRY,
        "Retry Fwd Roll",
      ),
    );
    menuItems.set(
      BfxBlotterAction.ACTION_DI_UNLOCK,
      new BfxInterventionContextMenuItem(BfxBlotterAction.ACTION_DI_UNLOCK, "Unlock"),
    );
    menuItems.set(
      BfxBlotterAction.ACTION_DI_VIEW_DETAILS,
      new BfxInterventionContextMenuItem(BfxBlotterAction.ACTION_DI_VIEW_DETAILS, "View Details"),
    );
    return menuItems;
  }

  protected getEnabledActions(
    quotes: Array<any>,
    selectedOrderIds: Array<string>,
  ): Array<BfxBlotterAction> {
    return [];
  }

  /**********************************************************************/
  /* METHODS - ACTION CONDITIONS */

  /* TRADER - ACCEPT (Available/Enabled) */
  protected checkTraderAcceptAvailableEnabled(item: CubiqTS.InterventionEventImpl): boolean {
    return (
      this.hasOrderData(item) &&
      item.eventType !== CubiqTS.InterventionEventType.FIXING_RATE &&
      (!this.isLocked(item) ||
        this.isMyLock(item) ||
        item.orderData.orderState == CubiqTS.OrderState.ACCEPTANCE_NEEDED) &&
      this.determineAcceptanceType(item.orderData) != null &&
      TRADER_ACCEPT_REJECT_REASONS.includes(item.reason) &&
      ((this.menuState.isTrader &&
        this.isTraderInGroup(item) &&
        this.menuState.canAcceptAsTrader) ||
        this.menuState.hasDITraderUnrestricted)
    );
  }

  /* TRADER - REJECT (Available/Enabled) */
  protected checkTraderRejectAvailableEnabled(item: CubiqTS.InterventionEventImpl): boolean {
    return (
      this.hasOrderData(item) &&
      item.eventType !== CubiqTS.InterventionEventType.FIXING_RATE &&
      (!this.isLocked(item) || this.isMyLock(item)) &&
      this.determineAcceptanceType(item.orderData) != null &&
      TRADER_ACCEPT_REJECT_REASONS.includes(item.reason) &&
      ((this.menuState.isTrader &&
        this.isTraderInGroup(item) &&
        this.menuState.canRejectAsTrader) ||
        this.menuState.hasDITraderUnrestricted)
    );
  }

  /* SALES - APPROVE (Available/Enabled) */
  protected checkSalesApproveAvailableEnabled(item: CubiqTS.InterventionEventImpl): boolean {
    return (
      !this.menuState.isTrader &&
      this.hasOrderData(item) &&
      item.eventType !== CubiqTS.InterventionEventType.FIXING_RATE &&
      (!this.isLocked(item) || this.isMyLock(item)) &&
      SALES_APPROVAL_REASONS.includes(item.reason) &&
      ((this.menuState.isSales &&
        this.isSalesInGroup(item.orderData) &&
        this.menuState.canApproveAsSales) ||
        this.menuState.canApproveUnrestrictedAsSales)
    );
  }

  /* SALES - REJECT (Available/Enabled) */
  protected checkSalesRejectAvailableEnabled(item: CubiqTS.InterventionEventImpl): boolean {
    return (
      !this.menuState.isTrader &&
      this.hasOrderData(item) &&
      item.eventType !== CubiqTS.InterventionEventType.FIXING_RATE &&
      (!this.isLocked(item) || this.isMyLock(item)) &&
      SALES_REJECT_REASONS.includes(item.reason) &&
      ((this.menuState.isSales &&
        this.isSalesInGroup(item.orderData) &&
        this.menuState.canRejectAsSales) ||
        (!this.menuState.isClient && this.menuState.canApproveUnrestrictedAsSales) ||
        (this.menuState.isClient &&
          this.isMyClient(item.orderData) &&
          this.menuState.canApproveUnrestrictedAsSales))
    );
  }

  /* CANCEL (Available) */
  protected checkCancelAvailable(item: CubiqTS.InterventionEventImpl): boolean {
    return item.eventType !== CubiqTS.InterventionEventType.FILL;
  }

  /* CANCEL (Enabled) */
  protected checkCancelEnabled(item: CubiqTS.InterventionEventImpl): boolean {
    return (
      this.hasOrderData(item) &&
      item.eventType !== CubiqTS.InterventionEventType.FIXING_RATE &&
      CANCEL_REASONS.includes(item.reason) &&
      ((this.menuState.isSales &&
        this.isSalesInGroup(item.orderData) &&
        this.menuState.canCancelOrder) ||
        this.menuState.canCancelAndModifyOrderUnrestricted)
    );
  }

  /* AMEND MARKUP (Enabled) */
  /**
   *  Amend Markup will be available to the user in the following cases:
   * - User is a Sales user.
   * - User is a member of the Sales Group the client is in.
   * - User has ROLE_DI_SALES_AMEND_MARKUP.
   * - Amend markup unavailable for entry user
   */
  protected checkAmendMarkupEnabled(item: CubiqTS.InterventionEventImpl): boolean {
    return (
      this.menuState.canAmendMarkup &&
      this.menuState.isSales &&
      this.menuState.count === 1 &&
      this.hasOrderData(item) &&
      item.eventType !== CubiqTS.InterventionEventType.FIXING_RATE &&
      AMEND_MARKUP_REASONS.includes(item.reason) &&
      (!this.isLocked(item) || this.isMyLock(item)) &&
      item.orderData.entryUserId.toString() !== this.activeUser.userId.toString() &&
      ((this.menuState.canEnterMarkupBmark && this.isOrderTypeBmark(item)) ||
        (this.menuState.canEnterMarkupLimits && this.isOrderTypeLimit(item)) ||
        (this.menuState.canEnterMarkupAlgo && this.isOrderTypeAlgo(item)))
    );
  }

  /* MANUAL PRICE (Enabled) */
  protected checkManualPriceEnabled(item: CubiqTS.InterventionEventImpl): boolean {
    return (
      this.menuState.isTrader &&
      this.menuState.canPriceFixRate &&
      item instanceof CubiqTS.InterventionEventImpl &&
      item.eventType === CubiqTS.InterventionEventType.FIXING_RATE &&
      (!this.isLocked(item) || this.isMyLock(item)) &&
      this.isTraderInGroup(item)
    );
  }

  /*Fwd Roll Price (Enabled) */
  protected checkFwdRollPriceEnabled(item: CubiqTS.InterventionEventImpl): boolean {
    return (
      this.menuState.canPriceFwdRoll &&
      item instanceof CubiqTS.InterventionEventImpl &&
      item.eventType === CubiqTS.InterventionEventType.PORTFOLIO_MANUAL_PRICING &&
      !this.isLocked(item)
    );
  }

  /*Fwd Roll Clear (Enabled) */
  protected checkFwdRollClearEnabled(item: CubiqTS.InterventionEventImpl): boolean {
    // We don;t check count here as we will do that inside prioritizeFwdRollItems
    return (
      this.menuState.canClearFwdRoll &&
      item instanceof CubiqTS.InterventionEventImpl &&
      item.eventType === CubiqTS.InterventionEventType.PORTFOLIO_MANUAL_PRICING &&
      !this.isLocked(item)
    );
  }

  /*Fwd Roll Retry (Enabled) */
  protected checkFwdRollRetryEnabled(item: CubiqTS.InterventionEventImpl): boolean {
    // We don;t check count here as we will do that inside prioritizeFwdRollItems
    return (
      this.menuState.canRetryFwdRoll &&
      item instanceof CubiqTS.InterventionEventImpl &&
      FWDROLL_RETRY_REASONS.includes(item.reason) &&
      !this.isLocked(item)
    );
  }

  /* UNLOCK (Enabled) */
  protected checkUnlockEnabled(item: CubiqTS.InterventionEventImpl): boolean {
    return this.menuState.canUnlockOrders && this.isLocked(item);
  }

  /* VIEW DETAILS (Enabled) */
  protected checkViewDetailsEnabled(item: CubiqTS.InterventionEventImpl): boolean {
    return (
      this.menuState.count === 1 &&
      item instanceof CubiqTS.InterventionEventImpl &&
      item.eventType !== CubiqTS.InterventionEventType.FIXING_RATE &&
      item.eventType !== CubiqTS.InterventionEventType.PORTFOLIO_MANUAL_PRICING
    );
  }

  /* DI Fill Approve (Available) */
  protected checkDIFillApproveAvailable(item: CubiqTS.InterventionEventImpl): boolean {
    return (
      this.hasOrderData(item) &&
      item.eventType === CubiqTS.InterventionEventType.FILL &&
      FILL_APPROVE_REASONS.includes(item.reason) &&
      ((this.menuState.isTrader && this.isTraderInGroup(item) && this.menuState.canDIApproveFill) ||
        this.menuState.hasDITraderUnrestricted)
    );
  }

  /* DI Fill Approve (Enabled) */
  protected checkDIFillApproveEnabled(item: CubiqTS.InterventionEventImpl): boolean {
    return (
      this.hasOrderData(item) &&
      item.eventType === CubiqTS.InterventionEventType.FILL &&
      FILL_APPROVE_REASONS.includes(item.reason) &&
      ((this.menuState.isTrader && this.isTraderInGroup(item) && this.menuState.canDIApproveFill) ||
        this.menuState.hasDITraderUnrestricted)
    );
  }

  /* DI Fill Cancel (Available) replaces generic cancel */
  protected checkDIFillCancelAvailable(item: CubiqTS.InterventionEvent): boolean {
    // this replaces cancel so condition is lax
    return (
      item.eventType === CubiqTS.InterventionEventType.FILL &&
      FILL_CANCEL_REASONS.includes(item.reason)
    );
  }

  /* DI Fill Cancel (Enabled) */
  protected checkDIFillCancelEnabled(item: CubiqTS.InterventionEventImpl): boolean {
    return (
      this.hasOrderData(item) &&
      item.eventType === CubiqTS.InterventionEventType.FILL &&
      FILL_CANCEL_REASONS.includes(item.reason) &&
      ((this.menuState.isTrader && this.isTraderInGroup(item) && this.menuState.canDICancelFill) ||
        this.menuState.hasDITraderUnrestricted)
    );
  }

  /* DI Fill Cancel (Available) replaces generic cancel */
  protected checkDIFillClearAvailable(item: CubiqTS.InterventionEvent): boolean {
    return (
      (item.eventType === CubiqTS.InterventionEventType.FILL &&
        FILL_CLEAR_REASONS.includes(item.reason)) ||
      item.eventType === CubiqTS.InterventionEventType.FWD_FILL_PENDING
    );
  }

  /* DI Fill Cancel (Enabled) */
  protected checkDIFillClearEnabled(item: CubiqTS.InterventionEventImpl): boolean {
    return (
      this.hasOrderData(item) &&
      (this.isFillClearEnabled(item) || this.isFwdFillPendingClearEnabled(item))
    );
  }

  isFwdFillPendingClearEnabled(item: CubiqTS.InterventionEventImpl): boolean {
    return (
      item.eventType === CubiqTS.InterventionEventType.FWD_FILL_PENDING &&
      ((this.menuState.isTrader && this.isTraderInGroup(item) && this.menuState.canDIClearFill) ||
        this.menuState.hasDITraderUnrestricted ||
        (this.menuState.isSales &&
          item.orderData != null &&
          this.isSalesInGroup(item.orderData) &&
          this.menuState.canClearAsSales) ||
        this.menuState.canApproveUnrestrictedAsSales)
    );
  }

  isFillClearEnabled(item: CubiqTS.InterventionEventImpl): boolean {
    return (
      item.eventType === CubiqTS.InterventionEventType.FILL &&
      FILL_CLEAR_REASONS.includes(item.reason) &&
      ((this.menuState.isTrader && this.isTraderInGroup(item) && this.menuState.canDIClearFill) ||
        this.menuState.hasDITraderUnrestricted)
    );
  }

  /* ******************************************************* */
  /* METHODS -  INTERVENTION & ORDER DATA HELPERS  */

  private isSalesInGroup(orderData: CubiqTS.OrderDataImpl): boolean {
    return (
      CubiqTS.UserType.SALES === this.activeUser.userTypeId &&
      orderData instanceof CubiqTS.OrderDataImpl &&
      this.activeUser.salesGroupsIdSet.has(orderData.salesGroupId.toString())
    );
  }

  private isBenchmark(orderData: CubiqTS.OrderDataImpl): boolean {
    return (
      orderData instanceof CubiqTS.OrderDataImpl &&
      orderData.orderType === CubiqTS.OrderType.BENCHMARK
    );
  }

  private isMyClient(orderData: CubiqTS.OrderDataImpl): boolean {
    return (
      CubiqTS.UserType.CLIENT === this.activeUser.userTypeId &&
      orderData instanceof CubiqTS.OrderDataImpl &&
      this.activeUser.clientCode === orderData.clientCode
    );
  }

  private isLocked(item: CubiqTS.InterventionEventImpl): boolean {
    let order: CubiqTS.OrderDataImpl = item.orderData;

    if (
      order instanceof CubiqTS.OrderDataImpl &&
      order.lockType == CubiqTS.LockType.MODIFY_LOCK &&
      MODIFY_LOCK_IGNORE_STATES.includes(order.orderState)
    ) {
      return false;
    }

    return item instanceof CubiqTS.InterventionEventImpl && item.lockUserId != null;
  }

  private isMyLock(item: CubiqTS.InterventionEventImpl): boolean {
    return item instanceof CubiqTS.InterventionEventImpl && item.lockUserId != null
      ? item.lockUserId.toString() === this.activeUser.userId
      : false;
  }

  private getOrderId(item: CubiqTS.InterventionEventImpl): string {
    return item instanceof CubiqTS.InterventionEventImpl &&
      item.orderData instanceof CubiqTS.OrderDataImpl
      ? item.orderData.orderId.toString()
      : null;
  }

  private hasOrderId(item: CubiqTS.InterventionEventImpl): boolean {
    return (
      item instanceof CubiqTS.InterventionEventImpl &&
      item.orderData instanceof CubiqTS.OrderDataImpl &&
      item.orderData.orderId instanceof CubiqTS.Long
    );
  }

  private hasOrderData(item: CubiqTS.InterventionEventImpl): boolean {
    return (
      item instanceof CubiqTS.InterventionEventImpl &&
      item.orderData instanceof CubiqTS.OrderDataImpl
    );
  }

  private isOrderTypeBmark(item: CubiqTS.InterventionEventImpl): boolean {
    return (
      this.hasOrderData(item) &&
      (item.orderData.orderType === CubiqTS.OrderType.BENCHMARK ||
        item.orderData.orderType === CubiqTS.OrderType.BMALGO)
    );
  }

  private isOrderTypeAlgo(item: CubiqTS.InterventionEventImpl): boolean {
    return this.hasOrderData(item) && item.orderData.orderType === CubiqTS.OrderType.ALGO;
  }
  private isOrderTypeLimit(item: CubiqTS.InterventionEventImpl): boolean {
    return (
      this.hasOrderData(item) &&
      (item.orderData.orderType === CubiqTS.OrderType.LIMIT_CALL ||
        item.orderData.orderType === CubiqTS.OrderType.LIMIT_TP ||
        item.orderData.orderType === CubiqTS.OrderType.LIMIT_SL)
    );
  }

  private isTraderInGroup(item: CubiqTS.InterventionEventImpl): boolean {
    if (
      item instanceof CubiqTS.InterventionEventImpl &&
      item.orderData instanceof CubiqTS.OrderDataImpl
    ) {
      if (this.isBenchmark(item.orderData)) {
        let traderGroupIds: Array<string> = Array.isArray(item.orderData.ownerGroupIds)
          ? item.orderData.ownerGroupIds.map((groupId) => groupId.toString())
          : null;
        if (Array.isArray(traderGroupIds) && traderGroupIds.length > 0) {
          let hasGroupMembership: boolean = false;
          for (let traderGroupId of traderGroupIds) {
            hasGroupMembership =
              hasGroupMembership || this.activeUser.tradingGroupsIdSet.has(traderGroupId);
          }
          return this.menuState.isTrader && hasGroupMembership;
        } else {
          return false;
        }
      } else {
        let deskId =
          (item.orderData as CubiqTS.OrderDataImpl).deskId != null
            ? (item.orderData as CubiqTS.OrderDataImpl).deskId.toString()
            : null;
        return (
          CubiqTS.UserType.TRADER === this.activeUser.userTypeId &&
          this.activeUser.tradingGroupsIdSet.has(deskId)
        );
      }
    } else if (
      item instanceof CubiqTS.InterventionEventImpl &&
      item.fixingRateRequirement instanceof CubiqTS.FixingRateRequirementDataImpl &&
      Array.isArray(item.fixingRateRequirement.traderGroupIds) &&
      item.eventType === CubiqTS.InterventionEventType.FIXING_RATE
    ) {
      let traderGroupIds: CubiqTS.Long[] = Array.isArray(item.fixingRateRequirement.traderGroupIds)
        ? item.fixingRateRequirement.traderGroupIds
        : [];
      return traderGroupIds
        .map((a) => a.toString())
        .some((tgId: string) => this.activeUser.tradingGroupsIdSet.has(tgId));
    } else {
      return false;
    }
  }

  private determineAcceptanceType(orderData: CubiqTS.OrderDataImpl): CubiqTS.AcceptanceType {
    let acceptanceType: CubiqTS.AcceptanceType = null;
    if (orderData instanceof CubiqTS.OrderDataImpl && orderData.orderState in CubiqTS.OrderState) {
      if (orderData.orderState == CubiqTS.OrderState.ACCEPTANCE_NEEDED) {
        acceptanceType = CubiqTS.AcceptanceType.ENTRY;
      } else if (orderData.orderState == CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED) {
        acceptanceType = CubiqTS.AcceptanceType.MODIFICATION;
      } else if (orderData.orderState == CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED) {
        acceptanceType = CubiqTS.AcceptanceType.CANCEL;
      }
    }
    return acceptanceType;
  }
}
